import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiCall } from 'services';
import type { 
  PackageUploadRequest,
  PackageUploadResponse,
  UploadProgress,
} from '../../types/package.types';

/**
 * API Error interface
 */
interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
    };
    status?: number;
  };
  status?: number;
}

/**
 * Upload progress callback type
 */
type UploadProgressCallback = (progress: UploadProgress) => void;

/**
 * Create FormData for file upload
 */
const createUploadFormData = (request: PackageUploadRequest): FormData => {
  const formData = new FormData();
  
  // Add file
  formData.append('file', request.file);
  
  // Add required fields
  formData.append('companyId', request.companyId);
  
  // Add optional fields
  if (request.packageName) {
    formData.append('packageName', request.packageName);
  }
  
  if (request.tagTypeId) {
    formData.append('tagTypeId', request.tagTypeId);
  }
  
  if (request.tagShopProductData) {
    formData.append('tagShopProductData', request.tagShopProductData);
  }

  if (request.variantId) {
    formData.append('variantId', request.variantId);
  }

  if (request.tagFamily) {
    formData.append('tagFamily', request.tagFamily);
  }

  if (request.batchSize) {
    formData.append('batchSize', request.batchSize.toString());
  }
  
  if (request.maxRows) {
    formData.append('maxRows', request.maxRows.toString());
}  
  
  return formData;
};

/**
 * Upload package file API call
 */
const uploadPackageFile = async (
  request: PackageUploadRequest,
  onProgress?: UploadProgressCallback
): Promise<PackageUploadResponse> => {
  const formData = createUploadFormData(request);
  
  const response = await createApiCall({
    method: 'POST',
    url: '/packages/upload',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    // Add upload progress tracking
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total && onProgress) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        
        onProgress({
          progress,
          stage: 'Uploading file...',
          processedRows: 0,
          totalRows: 0,
        });
      }
    },
  });
  
  return response.data;
};

/**
 * Package upload mutation options
 */
interface UsePackageUploadOptions {
  onProgress?: UploadProgressCallback;
  onSuccess?: (data: PackageUploadResponse) => void;
  onError?: (error: ApiError) => void;
}

/**
 * Enhanced hook for package upload mutation with proper typing
 * Replaces the complex Zustand state management with React Query
 */
export const usePackageUpload = (options?: UsePackageUploadOptions) => {
  const queryClient = useQueryClient();

  return useMutation<PackageUploadResponse, ApiError, PackageUploadRequest>({
    mutationKey: ['packageUpload'],
    
    mutationFn: (request) => uploadPackageFile(request, options?.onProgress),
    
    onMutate: () => {
      // Initial progress callback
      if (options?.onProgress) {
        options.onProgress({
          progress: 0,
          stage: 'Preparing upload...',
          processedRows: 0,
          totalRows: 0,
        });
      }
    },
    
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ 
        queryKey: ['packages'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['tags'] 
      });
      
      // Call custom success handler
      options?.onSuccess?.(data);
      
      console.log('Package upload successful:', {
        packageId: data.package.id,
        successfulTags: data.statistics.successfulTags,
        failedTags: data.statistics.failedTags,
        processingTime: data.statistics.processingTimeMs,
      });
    },
    
    onError: (error, variables) => {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Package upload failed';
      
      // Call custom error handler
      options?.onError?.(error);
      
      console.error('Package upload failed:', {
        error: errorMessage,
        fileName: variables.file.name,
        fileSize: variables.file.size,
        companyId: variables.companyId,
      });
    },
    
    // Mutation options
    retry: false, // Don't retry file uploads automatically
    gcTime: 5 * 60 * 1000, // Keep result in cache for 5 minutes
  });
};

/**
 * Hook for cancelling ongoing upload
 */
export const usePackageUploadCancel = () => {
  const queryClient = useQueryClient();

  return () => {
    // Cancel ongoing mutations
    queryClient.getQueryCache().clear();
    
    console.log('Package upload cancelled');
  };
};

/**
 * Query keys for package upload related queries
 */
export const packageUploadQueryKeys = {
  all: ['packageUpload'] as const,
  upload: () => [...packageUploadQueryKeys.all, 'upload'] as const,
} as const;